/* ==========================================================================
   peelou-selecteur-packs.js
   Surcouche de selection pour les fiches "pack" (application Simple Bundles).
   Lukas Brennan, developpeur front senior, dig-holding, 2026-09-30.
   Cheffe d'equipe : Julie Vasseur, UX lead.

   PRINCIPE. L'application Simple Bundles n'est ni reconfiguree ni remplacee.
   Elle continue de rendre ses <select> et de poser ses champs caches dans le
   formulaire panier. Ce fichier pose une couche au-dessus qui pilote ces
   <select> d'origine : il lit leurs <option>, en deduit les dimensions
   reelles, affiche une grille de boutons, puis ecrit dans select.value et
   emet un evenement 'change' bubbles pour que l'application suive.

   SOURCE DE VERITE. Une combinaison est disponible si et seulement si
   l'<option> correspondante du <select> d'origine ne porte pas l'attribut
   disabled. Rien d'autre n'est recalcule, aucun stock n'est interroge,
   aucune requete reseau n'est emise.

   PORTEE. Ce fichier contient DEUX modules independants, chacun avec sa
   propre garde :
   1. la surcouche des packs, qui ne fait rien du tout si la page ne contient
      pas #simple-bundles-io-options : elle sort immediatement, sans creer
      d'element, sans poser d'ecouteur, sans toucher au bouton d'achat ;
   2. la synchronisation de la barre collante, qui ne fait rien du tout si la
      page ne contient pas de .dg-sticky-bar portant a la fois un
      select[name="id"] et un button[name="add"]. Sur une fiche de pack la
      barre porte un lien et pas de formulaire : ce module sort aussi.
   ========================================================================== */

(function () {
  'use strict';

  var SEP = '\u001F';
  var COULEURS = ['vert', 'rouge', 'beige'];
  var COUPES = ['boxer', 'shorty'];

  function txt(v) { return v == null ? '' : String(v).trim(); }
  function bas(v) { return txt(v).toLowerCase(); }

  /* Decoupe une valeur d'option.
     Coupe sur le PREMIER tiret entoure d'espaces pour retirer le nom de la
     piece, puis decoupe le reste sur les barres obliques.
     "Boxer ou shorty - Vert / Boxer / 4 ans" donne piece "Boxer ou shorty" et
     parts ["Vert", "Boxer", "4 ans"].
     "Insert - 1 ( 4-6 ans )" donne piece "Insert" et parts ["1 ( 4-6 ans )"].
     Le tiret de "T-shirt" et celui de "4-6" ne sont pas entoures d'espaces,
     ils ne sont donc jamais pris pour le separateur. */
  function decoupe(valeur) {
    var i = valeur.indexOf(' - ');
    var piece = '';
    var reste = valeur;
    if (i > -1) {
      piece = txt(valeur.slice(0, i));
      reste = valeur.slice(i + 3);
    }
    return { piece: piece, parts: reste.split('/').map(txt) };
  }

  /* Nomme une dimension par son CONTENU, jamais par sa position. */
  function nomDimension(valeurs) {
    var b = valeurs.map(bas);
    if (b.every(function (v) { return COULEURS.indexOf(v) > -1; })) return 'Couleur';
    if (b.every(function (v) { return COUPES.indexOf(v) > -1; })) return 'Coupe';
    return 'Taille';
  }

  function liste(valeurs) {
    if (valeurs.length === 1) return valeurs[0];
    return valeurs.slice(0, -1).join(', ') + ' ou ' + valeurs[valeurs.length - 1];
  }

  /* ---------------------------------------------------------------- modele */

  function construireModele(select) {
    var lignes = [];
    var piece = '';
    var arites = {};
    var i;

    for (i = 0; i < select.options.length; i++) {
      var o = select.options[i];
      if (!o.value) continue; /* option vide : marque-place de l'application */
      var d = decoupe(o.value);
      if (!piece && d.piece) piece = d.piece;
      arites[d.parts.length] = true;
      lignes.push({ valeur: o.value, parts: d.parts, indispo: o.disabled });
    }

    if (!lignes.length) return null;
    /* Arite incoherente entre options : on ne touche pas a ce select. */
    if (Object.keys(arites).length !== 1) return null;

    var k = lignes[0].parts.length;
    if (k < 1) return null;

    var dims = [];
    for (i = 0; i < k; i++) {
      dims.push({ valeurs: valeursDe(lignes, i) });
    }
    dims.forEach(function (d) { d.nom = nomDimension(d.valeurs); });

    /* Deux dimensions de meme nom sur un meme select : on les distingue par
       leur rang, seul fait disponible. Ne se produit pas sur le catalogue
       mesure le 2026-09-30, la branche est la par prudence. */
    dims.forEach(function (d, idx) {
      var doublons = dims.filter(function (e) { return e.nom === d.nom; }).length;
      if (doublons > 1) d.nom = d.nom + ' ' + (idx + 1);
    });

    var combis = Object.create(null);
    lignes.forEach(function (l) { combis[l.parts.join(SEP)] = l; });

    return {
      select: select,
      piece: piece,
      dims: dims,
      combis: combis,
      sel: dims.map(function () { return null; }),
      noeuds: [],
      message: null
    };
  }

  function valeursDe(lignes, index) {
    var vals = [];
    lignes.forEach(function (l) {
      if (vals.indexOf(l.parts[index]) === -1) vals.push(l.parts[index]);
    });
    return vals;
  }

  /* Cherche une combinaison DISPONIBLE compatible avec les contraintes.
     contraintes[j] valant null signifie "libre". */
  function existe(modele, contraintes) {
    for (var key in modele.combis) {
      var l = modele.combis[key];
      if (l.indispo) continue;
      var ok = true;
      for (var j = 0; j < contraintes.length; j++) {
        if (contraintes[j] !== null && l.parts[j] !== contraintes[j]) { ok = false; break; }
      }
      if (ok) return l;
    }
    return null;
  }

  function disponible(modele, i, v) {
    var c = modele.sel.slice();
    c[i] = v;
    return existe(modele, c) !== null;
  }

  /* Pour une valeur refusee, cherche les sorties : quelle autre dimension
     deja choisie faudrait-il changer, et pour quelles valeurs. */
  function sorties(modele, i, v) {
    var res = [];
    modele.dims.forEach(function (dim, j) {
      if (j === i || modele.sel[j] === null) return;
      var alternatives = [];
      dim.valeurs.forEach(function (w) {
        if (w === modele.sel[j]) return;
        var c = modele.sel.slice();
        c[i] = v;
        c[j] = w;
        if (existe(modele, c)) alternatives.push(w);
      });
      if (alternatives.length) {
        res.push({ dim: j, actuelle: modele.sel[j], valeurs: alternatives });
      }
    });
    return res;
  }

  /* ------------------------------------------------------------------ vue */

  var modeles = [];
  var annonce = null;
  var boutonsAchat = [];
  var boutonsPaiement = [];

  function annoncer(message) {
    if (!annonce) return;
    annonce.textContent = '';
    /* Reecriture differee : une region dont le contenu est remplace dans le
       meme cycle n'est pas toujours relue. */
    window.setTimeout(function () { annonce.textContent = message; }, 40);
  }

  function rendre(modele) {
    var enveloppe = document.createElement('div');
    enveloppe.className = 'peelou-sp';
    enveloppe.setAttribute('data-pour', modele.select.name);

    modele.dims.forEach(function (dim, i) {
      var bloc = document.createElement('div');
      bloc.className = 'peelou-sp-dim';

      var intitule = (modele.piece ? modele.piece + ', ' : '') + dim.nom.toLowerCase();

      var legende = document.createElement('div');
      legende.className = 'peelou-sp-legende';
      var nomPiece = document.createElement('span');
      nomPiece.className = 'peelou-sp-piece';
      nomPiece.textContent = intitule;
      legende.appendChild(nomPiece);
      var etat = document.createElement('span');
      etat.className = 'peelou-sp-achoisir';
      legende.appendChild(etat);
      bloc.appendChild(legende);

      var grille = document.createElement('div');
      grille.className = 'peelou-sp-valeurs';
      grille.setAttribute('role', 'group');
      grille.setAttribute('aria-label', intitule);

      var estCouleur = dim.nom.indexOf('Couleur') === 0;
      var boutons = [];

      dim.valeurs.forEach(function (v) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'peelou-sp-val';
        b.setAttribute('data-valeur', v);
        if (estCouleur) {
          var pastille = document.createElement('span');
          pastille.className = 'peelou-sp-pastille';
          pastille.setAttribute('data-couleur', bas(v));
          pastille.setAttribute('aria-hidden', 'true');
          b.appendChild(pastille);
        }
        var nom = document.createElement('span');
        nom.className = 'peelou-sp-libelle';
        /* Le bouton n'affiche QUE la valeur de sa dimension. */
        nom.textContent = v;
        b.appendChild(nom);
        b.addEventListener('click', function () { auClic(modele, i, v); });
        grille.appendChild(b);
        boutons.push(b);
      });

      bloc.appendChild(grille);
      enveloppe.appendChild(bloc);
      modele.noeuds.push({ etat: etat, boutons: boutons, intitule: intitule });
    });

    var message = document.createElement('div');
    message.className = 'peelou-sp-message';
    message.hidden = true;
    enveloppe.appendChild(message);
    modele.message = message;

    var label = null;
    if (modele.select.id) {
      label = modele.select.parentNode.querySelector(
        'label[for="' + modele.select.id.replace(/"/g, '\\"') + '"]'
      );
    }
    if (label) label.classList.add('peelou-sp-source-label');
    /* Le select d'origine n'est jamais supprime : il est retire du flux
       visuel et reste dans le DOM comme source de verite. */
    modele.select.classList.add('peelou-sp-source');
    modele.select.parentNode.insertBefore(enveloppe, label || modele.select);
  }

  function cacherMessage(modele) {
    modele.message.hidden = true;
    while (modele.message.firstChild) modele.message.removeChild(modele.message.firstChild);
  }

  function auClic(modele, i, v) {
    if (disponible(modele, i, v)) {
      modele.sel[i] = v;
      cacherMessage(modele);
      appliquer(modele);
      rafraichir();
      annoncer(modele.noeuds[i].intitule + ' : ' + v + '.');
      return;
    }
    refuser(modele, i, v);
  }

  function refuser(modele, i, v) {
    var s = sorties(modele, i, v);
    var phrases = [];
    cacherMessage(modele);

    if (s.length) {
      s.forEach(function (o) {
        phrases.push(
          v + ' n\u2019est pas disponible en ' + o.actuelle +
          '. Disponible en ' + liste(o.valeurs) + '.'
        );
      });
    } else {
      var poses = modele.sel.filter(function (x) { return x !== null; });
      if (!poses.length) {
        phrases.push(v + ' n\u2019est disponible dans aucune combinaison.');
      } else {
        phrases.push(
          v + ' n\u2019est pas disponible avec ' + liste(poses) +
          ', et aucun autre choix ne le rend disponible.'
        );
      }
    }

    var p = document.createElement('p');
    p.className = 'peelou-sp-fait';
    p.textContent = phrases.join(' ');
    modele.message.appendChild(p);

    if (s.length) {
      var zone = document.createElement('div');
      zone.className = 'peelou-sp-sorties';
      s.forEach(function (o) {
        o.valeurs.forEach(function (w) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'peelou-sp-sortie';
          b.textContent = 'Prendre ' + v + ' en ' + w;
          b.addEventListener('click', function () {
            /* Bascule la dimension bloquante EN CONSERVANT la valeur demandee. */
            modele.sel[o.dim] = w;
            modele.sel[i] = v;
            cacherMessage(modele);
            appliquer(modele);
            rafraichir();
            annoncer(v + ' en ' + w + ' : choix appliqu\u00e9.');
          });
          zone.appendChild(b);
        });
      });
      modele.message.appendChild(zone);
    }

    modele.message.hidden = false;
    annoncer(phrases.join(' '));
  }

  /* Ecrit dans le select d'origine, seule source de verite pour
     l'application, puis emet l'evenement que celle-ci ecoute. */
  function appliquer(modele) {
    var cible = '';
    var complet = modele.sel.every(function (x) { return x !== null; });
    if (complet) {
      var l = modele.combis[modele.sel.join(SEP)];
      if (l) cible = l.valeur;
    }
    if (modele.select.value === cible) return;
    modele.select.value = cible;
    modele.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function rafraichir() {
    modeles.forEach(function (modele) {
      modele.dims.forEach(function (dim, i) {
        var n = modele.noeuds[i];
        dim.valeurs.forEach(function (v, k) {
          var b = n.boutons[k];
          b.setAttribute('aria-pressed', modele.sel[i] === v ? 'true' : 'false');
          if (disponible(modele, i, v)) {
            b.removeAttribute('aria-disabled');
            b.setAttribute('aria-label', v);
          } else {
            /* aria-disabled, jamais disabled : la valeur reste affichee,
               atteignable au clavier, et doit repondre au clic. */
            b.setAttribute('aria-disabled', 'true');
            b.setAttribute('aria-label', v + ', indisponible');
          }
        });
        if (modele.sel[i] === null) {
          n.etat.className = 'peelou-sp-achoisir';
          n.etat.textContent = ' : \u00e0 choisir';
        } else {
          n.etat.className = 'peelou-sp-choisi';
          n.etat.textContent = ' : ' + modele.sel[i];
        }
      });
    });
    majAchat();
  }

  function manquants() {
    var res = [];
    modeles.forEach(function (modele) {
      modele.dims.forEach(function (dim, i) {
        if (modele.sel[i] === null) res.push({ piece: modele.piece, nom: dim.nom });
      });
    });
    return res;
  }

  function majAchat() {
    var m = manquants();
    var complet = m.length === 0;
    var raison = '';

    if (!complet) {
      raison = 'Choisissez la ' + m[0].nom.toLowerCase() +
        (m[0].piece ? ' (' + m[0].piece + ')' : '');
      if (m.length > 1) {
        raison += ' et ' + (m.length - 1) + ' autre' + (m.length - 1 > 1 ? 's' : '') + ' choix';
      }
    }

    boutonsAchat.forEach(function (o) {
      if (complet) {
        o.bouton.disabled = o.disabledOrigine;
        o.bouton.removeAttribute('aria-disabled');
        o.bouton.classList.remove('peelou-sp-achat-bloque');
        if (o.libelle) o.libelle.textContent = o.texteOrigine;
      } else {
        o.bouton.disabled = true;
        o.bouton.setAttribute('aria-disabled', 'true');
        o.bouton.classList.add('peelou-sp-achat-bloque');
        /* Un bouton d'achat ne ment jamais : il porte la raison du blocage. */
        if (o.libelle) o.libelle.textContent = raison;
      }
    });

    /* Le paiement accelere contourne le bouton d'ajout : il est neutralise
       tant que l'ensemble n'est pas complet, sinon il permettrait d'acheter
       un pack sans aucune taille. */
    boutonsPaiement.forEach(function (zone) {
      if (complet) zone.classList.remove('peelou-sp-paiement-bloque');
      else zone.classList.add('peelou-sp-paiement-bloque');
      zone.setAttribute('aria-hidden', complet ? 'false' : 'true');
      var enfants = zone.querySelectorAll('button, [role="button"]');
      for (var i = 0; i < enfants.length; i++) {
        if (complet) {
          enfants[i].removeAttribute('disabled');
          enfants[i].removeAttribute('aria-disabled');
        } else {
          enfants[i].setAttribute('disabled', 'disabled');
          enfants[i].setAttribute('aria-disabled', 'true');
        }
      }
    });
  }

  /* ----------------------------------------------------------------- init */

  function init() {
    var conteneur = document.getElementById('simple-bundles-io-options');
    if (!conteneur) return; /* aucun effet hors des fiches a bloc d'application */

    var selects = conteneur.querySelectorAll('select[name^="properties["]');
    if (!selects.length) return;

    var i;
    for (i = 0; i < selects.length; i++) {
      var modele = construireModele(selects[i]);
      /* select non interpretable : on laisse l'original visible et intact */
      if (!modele) continue;
      modeles.push(modele);
      rendre(modele);
    }
    if (!modeles.length) return;

    annonce = document.createElement('p');
    annonce.className = 'peelou-sp-annonce';
    annonce.setAttribute('role', 'status');
    annonce.setAttribute('aria-live', 'polite');
    conteneur.insertBefore(annonce, conteneur.firstChild);

    /* Seuls les boutons d'achat du PRODUIT COURANT sont concernes. Les fiches
       de produits lies rendent elles aussi des button[name="add"] (les
       "Choisir des options" des cartes et de l'ajout rapide) : les bloquer
       reviendrait a empecher l'achat d'un autre produit parce que le pack
       n'est pas complet. Mesure du 2026-09-30 : la fiche de pack n'en porte
       aucun aujourd'hui, mais rien ne garantit que cela dure. */
    var achats = document.querySelectorAll('button[name="add"]');
    for (i = 0; i < achats.length; i++) {
      if (achats[i].closest('.card, .card-wrapper, .quick-add, quick-add-modal')) continue;
      var libelle = achats[i].querySelector('span');
      boutonsAchat.push({
        bouton: achats[i],
        libelle: libelle,
        texteOrigine: libelle ? libelle.textContent : '',
        disabledOrigine: achats[i].disabled
      });
    }

    var paiements = document.querySelectorAll('.shopify-payment-button');
    for (i = 0; i < paiements.length; i++) boutonsPaiement.push(paiements[i]);

    /* Aucune preselection. Les <option selected> posees par l'application sont
       remises a vide, et le 'change' emis vide les champs caches qu'elle a
       deja ecrits dans le formulaire panier. Le client choisit. */
    modeles.forEach(function (m) {
      m.sel = m.dims.map(function () { return null; });
      appliquer(m);
    });

    rafraichir();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================================================
   MODULE 2 : SYNCHRONISATION DE LA BARRE COLLANTE
   Lukas Brennan, dig-holding, 2026-09-30, sur mission de Julie Vasseur.

   LE DEFAUT CORRIGE. snippets/dg-stickybar.liquid rend son select[name="id"]
   et son bouton une fois, cote serveur. Un changement de variante cote client
   ne les touchait jamais. Mesure du 2026-09-30 sur boxer-ou-shorty : apres
   avoir choisi Beige puis M, la page passait bien a 51591614300509 (epuisee,
   bouton principal "Epuise" et desactive) tandis que la barre restait sur
   49098415636829, "Vert / Boxer / 4 ans", avec un bouton actif. Le client
   pouvait donc acheter un produit different de celui qu'il regardait.

   SOURCE DE VERITE : celle du theme, pas la mienne. La variante affichee est
   lue dans le champ cache input[name="id"] du formulaire principal, que le
   theme met a jour a chaque changement. L'etat et le libelle du bouton sont
   COPIES depuis le bouton principal, donc traduits par le theme et non
   reecrits ici. La disponibilite n'est jamais recalculee.

   AUCUNE BOUCLE POSSIBLE : le MutationObserver surveille le conteneur
   d'information produit, et les seules ecritures faites ici visent la barre
   collante, qui est en dehors de ce conteneur.

   SENS UNIQUE, ASSUME : page vers barre. Un client qui changerait de variante
   depuis le select de la barre elle-meme ne fait pas suivre la page. Ce sens
   n'etait pas demande et n'est pas traite ici.
   ========================================================================== */

(function () {
  'use strict';

  function init() {
    var barre = document.querySelector('.dg-sticky-bar');
    if (!barre) return;

    var select = barre.querySelector('select[name="id"]');
    var bouton = barre.querySelector('button[name="add"]');
    /* Fiche de pack : la barre porte un lien "Choisir les tailles" et aucun
       formulaire. Rien a synchroniser, on sort. */
    if (!select || !bouton) return;

    var etiquette = bouton.querySelector('.addToCartText') || bouton;
    var texteOrigine = etiquette.textContent.trim();
    var planifie = false;

    function idAffiche() {
      var champ = document.querySelector(
        'product-form input[name="id"], form[action*="/cart/add"] input[name="id"]'
      );
      if (champ && champ.value) return champ.value;
      try {
        var v = new URL(window.location.href).searchParams.get('variant');
        if (v) return v;
      } catch (e) { /* URL non analysable : on s'en passe */ }
      return null;
    }

    /* Le libelle traduit est deja rendu par le theme dans l'option elle-meme :
       "Vert / Boxer / 6 ans - 19,00 EUR - Epuise". On prend le dernier segment
       plutot que d'ecrire une traduction en dur dans ce fichier. */
    function libelleEpuise(option) {
      var parts = option.textContent.split(' - ');
      return parts.length > 1 ? parts[parts.length - 1].trim() : texteOrigine;
    }

    function synchroniser() {
      var id = idAffiche();
      if (!id) return;

      var option = null;
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].value === id) { option = select.options[i]; break; }
      }
      /* Variante inconnue de la barre : on ne devine rien, on ne touche a rien. */
      if (!option) return;

      if (select.value !== id) {
        select.value = id;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      var principal = document.querySelector('product-form button[name="add"]');
      var indisponible = principal ? principal.disabled : option.disabled;
      var libelle = '';
      if (principal) {
        var s = principal.querySelector('span');
        if (s) libelle = s.textContent.trim();
      }
      if (!libelle) libelle = indisponible ? libelleEpuise(option) : texteOrigine;

      if (bouton.disabled !== indisponible) bouton.disabled = indisponible;
      if (indisponible) bouton.setAttribute('aria-disabled', 'true');
      else bouton.removeAttribute('aria-disabled');
      if (indisponible) bouton.classList.add('no-stock');
      else bouton.classList.remove('no-stock');
      if (etiquette.textContent.trim() !== libelle) etiquette.textContent = libelle;
    }

    function planifier() {
      if (planifie) return;
      planifie = true;
      window.setTimeout(function () { planifie = false; synchroniser(); }, 60);
    }

    var cible = document.querySelector('.product__info-container') ||
      document.querySelector('product-info') ||
      document.body;

    new MutationObserver(planifier).observe(cible, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'disabled', 'class']
    });

    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest && e.target.closest('variant-selects')) planifier();
    }, true);
    window.addEventListener('popstate', planifier);

    synchroniser();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
